import React from 'react';

/**
 * HealthMetric - Component for displaying key health indicators and statistics
 * Used for vital signs, appointment counts, and other quantitative health data
 */
interface HealthMetricProps {
  label: string;
  value: string | number;
  unit?: string;
  trend?: {
    direction: 'up' | 'down' | 'stable';
    percentage: number;
    period: string;
  };
  status?: 'normal' | 'warning' | 'critical';
  icon?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const HealthMetric: React.FC<HealthMetricProps> = ({
  label,
  value,
  unit,
  trend,
  status = 'normal',
  icon,
  size = 'md',
  className = ''
}) => {
  const statusColors = {
    normal: 'text-gray-900',
    warning: 'text-yellow-700',
    critical: 'text-red-700'
  };

  const statusBgColors = {
    normal: 'bg-gray-50',
    warning: 'bg-yellow-50',
    critical: 'bg-red-50'
  };

  const trendIcons = {
    up: '↗️',
    down: '↘️',
    stable: '➡️'
  };

  const trendColors = {
    up: 'text-green-600',
    down: 'text-red-600',
    stable: 'text-gray-600'
  };

  const sizeClasses = {
    sm: {
      container: 'p-3',
      value: 'text-lg',
      label: 'text-xs',
      trend: 'text-xs'
    },
    md: {
      container: 'p-4',
      value: 'text-2xl',
      label: 'text-sm',
      trend: 'text-sm'
    },
    lg: {
      container: 'p-6',
      value: 'text-3xl',
      label: 'text-base',
      trend: 'text-base'
    }
  };

  const classes = sizeClasses[size];

  return (
    <div className={`${statusBgColors[status]} rounded-lg border border-gray-200 ${classes.container} ${className}`}>
      {/* Header with icon */}
      <div className="flex items-center justify-between mb-2">
        <span className={`${classes.label} font-medium text-gray-600`}>
          {label}
        </span>
        {icon && (
          <div className="text-gray-400">
            {icon}
          </div>
        )}
      </div>

      {/* Value */}
      <div className="flex items-baseline">
        <span className={`${classes.value} font-bold ${statusColors[status]}`}>
          {value}
        </span>
        {unit && (
          <span className={`ml-1 ${classes.label} text-gray-500`}>
            {unit}
          </span>
        )}
      </div>

      {/* Trend indicator */}
      {trend && (
        <div className={`flex items-center mt-2 ${classes.trend}`}>
          <span className="mr-1" role="img" aria-hidden="true">
            {trendIcons[trend.direction]}
          </span>
          <span className={trendColors[trend.direction]}>
            {trend.percentage}%
          </span>
          <span className="text-gray-500 ml-1">
            {trend.period}
          </span>
        </div>
      )}
    </div>
  );
};

export default HealthMetric;